import React from 'react';
import { MenuAction, MenuContext } from '@/types/MenuTypes';
import { toast } from '@/components/ui/use-toast';

export class MenuActionHandler {
  private windowManager: any;
  private taskManager: any;
  private notesManager: any;
  private lockScreen?: () => void;
  private logout?: () => void;
  
  constructor(
    windowManager: any, 
    taskManager?: any, 
    notesManager?: any,
    lockScreen?: () => void,
    logout?: () => void
  ) {
    this.windowManager = windowManager;
    this.taskManager = taskManager;
    this.notesManager = notesManager;
    this.lockScreen = lockScreen;
    this.logout = logout;
  }

  handleMenuAction = (actionId: string, context?: MenuContext) => {
    const { windowId, appType } = context || {};
    
    switch (actionId) {
      // Apple menu actions
      case 'about-open-insurance-os':
        this.handleAboutOpenInsuranceOS();
        break;
      case 'account-settings':
        this.handleAccountSettings();
        break;
      case 'os-settings':
        this.handleOSSettings();
        break;
      case 'billing-subscription':
        this.handleBillingSubscription();
        break;
      case 'notification-preferences':
        this.handleNotificationPreferences();
        break;
      case 'security-settings':
        this.handleSecuritySettings();
        break;
      case 'switch-organization':
        this.handleSwitchOrganization();
        break;
      case 'lock-open-insurance-os':
        this.handleLockOpenInsuranceOS();
        break;
      case 'logout':
        this.handleLogout();
        break;
        
      // System-wide actions
      case 'new-task':
        this.handleNewTask();
        break;
      case 'new-note':
        this.handleNewNote();
        break;
      
      // File actions
      case 'save':
        this.handleSave(windowId);
        break;
      case 'export':
        this.handleExport(windowId);
        break;
      case 'new-folder':
        this.handleNewFolder(windowId);
        break;
      
      // Edit actions  
      case 'undo':
        this.handleUndo(windowId);
        break;
      case 'redo':
        this.handleRedo(windowId);
        break;
      case 'cut':
        this.handleCut(windowId);
        break;
      case 'copy':
        this.handleCopy(windowId);
        break;
      case 'paste':
        this.handlePaste(windowId);
        break;
      case 'select-all':
        this.handleSelectAll(windowId);
        break;
      case 'find':
        this.handleFind(windowId);
        break;
      
      // View actions
      case 'show-sidebar':
        this.handleToggleSidebar(windowId);
        break;
      case 'fullscreen':
        this.handleFullscreen(windowId);
        break;
      case 'refresh':
        this.handleRefresh(windowId);
        break;
      
      // Window actions
      case 'minimize':
        this.handleMinimize(windowId);
        break;
      case 'zoom':
        this.handleZoom(windowId);
        break;
      case 'bring-all-front':
        this.handleBringAllToFront();
        break;
      
      default:
        console.log(`Menu action "${actionId}" not implemented`);
        toast({
          title: "Action not available",
          description: `The "${actionId}" action is not yet implemented.`,
        });
    }
  };

  private handleNewTask = () => {
    import('@/components/windows/TaskCreatorWindow').then(({ default: TaskCreatorWindow }) => {
      import('@/lib/AppMenuRegistry').then(({ taskCreatorMenuConfig }) => {
        this.windowManager.openWindow(
          'task-creator',
          'Create New Task',
          React.createElement(TaskCreatorWindow, { onClose: () => this.windowManager.closeWindow('task-creator') }),
          { 
            appType: 'task-creator',
            menuConfig: taskCreatorMenuConfig
          }
        );
      });
    });
  };

  private handleNewNote = () => {
    import('@/components/windows/NotesWindow').then(({ default: NotesWindow }) => {
      import('@/lib/AppMenuRegistry').then(({ notesMenuConfig }) => {
        this.windowManager.openWindow(
          'notes',
          'Notes',
          React.createElement(NotesWindow),
          { 
            appType: 'notes',
            menuConfig: notesMenuConfig
          }
        );
      });
    });
  };

  private handleSave = (windowId?: string) => {
    // Trigger save action for the specific window/app
    const event = new CustomEvent('menuAction', { detail: { action: 'save', windowId } });
    window.dispatchEvent(event);
  };

  private handleExport = (windowId?: string) => {
    const event = new CustomEvent('menuAction', { detail: { action: 'export', windowId } });
    window.dispatchEvent(event);
  };

  private handleNewFolder = (windowId?: string) => {
    const event = new CustomEvent('menuAction', { detail: { action: 'newFolder', windowId } });
    window.dispatchEvent(event);
  };

  private handleUndo = (windowId?: string) => {
    document.execCommand('undo');
  };

  private handleRedo = (windowId?: string) => {
    document.execCommand('redo');
  };

  private handleCut = (windowId?: string) => {
    document.execCommand('cut');
  };

  private handleCopy = (windowId?: string) => {
    document.execCommand('copy');
  };

  private handlePaste = (windowId?: string) => {
    document.execCommand('paste');
  };

  private handleSelectAll = (windowId?: string) => {
    document.execCommand('selectAll');
  };

  private handleFind = (windowId?: string) => {
    const event = new CustomEvent('menuAction', { detail: { action: 'find', windowId } });
    window.dispatchEvent(event);
  };

  private handleToggleSidebar = (windowId?: string) => {
    const event = new CustomEvent('menuAction', { detail: { action: 'toggleSidebar', windowId } });
    window.dispatchEvent(event);
  };

  private handleFullscreen = (windowId?: string) => {
    if (windowId) {
      this.windowManager.maximizeWindow(windowId);
    } else {
      document.documentElement.requestFullscreen();
    }
  };

  private handleRefresh = (windowId?: string) => {
    const event = new CustomEvent('menuAction', { detail: { action: 'refresh', windowId } });
    window.dispatchEvent(event);
  };

  private handleMinimize = (windowId?: string) => {
    if (windowId) {
      this.windowManager.minimizeWindow(windowId);
    }
  };

  private handleZoom = (windowId?: string) => {
    if (windowId) {
      this.windowManager.maximizeWindow(windowId);
    }
  };

  private handleBringAllToFront = () => {
    // Bring all windows to front
    this.windowManager.windows.forEach((window: any) => {
      if (window.isMinimized) {
        this.windowManager.restoreWindow(window.id);
      }
    });
  };

  // Apple menu action handlers
  private handleAboutOpenInsuranceOS = () => {
    const event = new CustomEvent('showAboutDialog');
    window.dispatchEvent(event);
  };

  private handleAccountSettings = () => {
    import('@/components/windows/AccountManagementWindow').then(({ default: AccountManagementWindow }) => {
      this.windowManager.openWindow(
        'account-management',
        'Account Settings',
        React.createElement(AccountManagementWindow, { 
          onClose: () => this.windowManager.closeWindow('account-management')
        }),
        { 
          appType: 'account-management',
          menuConfig: {}
        }
      );
    });
  };

  private handleOSSettings = () => {
    import('@/components/windows/OSSettingsWindow').then(({ OSSettingsWindow }) => {
      this.windowManager.openWindow(
        'os-settings',
        'OS Settings',
        React.createElement(OSSettingsWindow, { 
          onClose: () => this.windowManager.closeWindow('os-settings')
        }),
        { 
          appType: 'os-settings',
          menuConfig: {}
        }
      );
    });
  };

  private handleBillingSubscription = () => {
    // Redirect to account settings - Clerk handles billing section
    this.handleAccountSettings();
  };

  private handleNotificationPreferences = () => {
    import('@/components/windows/NotificationSettingsWindow').then(({ default: NotificationSettingsWindow }) => {
      this.windowManager.openWindow(
        'notification-settings',
        'Notification Settings',
        React.createElement(NotificationSettingsWindow, { 
          onClose: () => this.windowManager.closeWindow('notification-settings')
        }),
        { 
          appType: 'notification-settings',
          size: { width: 900, height: 700 },
          position: { x: 100, y: 100 },
          menuConfig: {}
        }
      );
    });
  };

  private handleSecuritySettings = () => {
    // Redirect to account settings - Clerk handles security section
    this.handleAccountSettings();
  };

  private handleSwitchOrganization = () => {
    // Redirect to account settings - Clerk handles organizations section
    this.handleAccountSettings();
  };

  private handleLockOpenInsuranceOS = () => {
    if (this.lockScreen) {
      this.lockScreen();
    } else {
      toast({
        title: "Lock function not available",
        description: "Lock screen functionality is not configured.",
        variant: "destructive",
      });
    }
  };

  private handleLogout = () => {
    if (this.logout) {
      this.logout();
    } else {
      // Fallback: trigger a logout event
      const event = new CustomEvent('triggerLogout');
      window.dispatchEvent(event);
    }
  };
}